/******
 * This file was generated by a Rake task.
 * Do not modify this file manually.
 * Add changes to the `Submittal.ts` file.
 ******/
// oxlint-disable-next-line no-unused-vars
import dayjs, { Dayjs } from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
dayjs.extend(utc);
dayjs.extend(timezone);

import { Submittal } from "./Submittal";

import { MaterialsSubmittal } from "./MaterialsSubmittal";
import { Project } from "./Project";
import { VendorCache } from "./VendorCache";

export abstract class SubmittalModel {
    id: number;
    approvalDate: Dayjs | null;
    createdAt: Dayjs;
    currentRevision: boolean;
    description: string | null;
    label: string | null;
    latestResponse: string;
    leadTime: number | null;
    needsReviseAndResubmit: boolean;
    projectId: number | null;
    requiredOnJobsite: Dayjs | null;
    specNumber: string | null;
    status: string | null;
    submitDate: Dayjs | null;
    submittalNumber: string | null;
    submittalType: string | null;
    title: string | null;
    updatedAt: Dayjs;
    vendorUniqueId: string | null;
    allMaterialsSubmittals: MaterialsSubmittal[] | undefined;
    materialsSubmittals: MaterialsSubmittal[] | undefined;
    project: Project | null | undefined;
    vendorCache: VendorCache | null | undefined;
    _destroy: boolean | undefined;

    /* eslint-disable max-statements */
    constructor(
        id: number,
        approvalDate: Dayjs | null,
        createdAt: Dayjs,
        currentRevision: boolean,
        description: string | null,
        label: string | null,
        latestResponse: string,
        leadTime: number | null,
        needsReviseAndResubmit: boolean,
        projectId: number | null,
        requiredOnJobsite: Dayjs | null,
        specNumber: string | null,
        status: string | null,
        submitDate: Dayjs | null,
        submittalNumber: string | null,
        submittalType: string | null,
        title: string | null,
        updatedAt: Dayjs,
        vendorUniqueId: string | null,
        allMaterialsSubmittals: MaterialsSubmittal[] | undefined,
        materialsSubmittals: MaterialsSubmittal[] | undefined,
        project: Project | null | undefined,
        vendorCache: VendorCache | null | undefined
    ) {
        this.id = id;
        this.approvalDate = approvalDate;
        this.createdAt = createdAt;
        this.currentRevision = currentRevision;
        this.description = description;
        this.label = label;
        this.latestResponse = latestResponse;
        this.leadTime = leadTime;
        this.needsReviseAndResubmit = needsReviseAndResubmit;
        this.projectId = projectId;
        this.requiredOnJobsite = requiredOnJobsite;
        this.specNumber = specNumber;
        this.status = status;
        this.submitDate = submitDate;
        this.submittalNumber = submittalNumber;
        this.submittalType = submittalType;
        this.title = title;
        this.updatedAt = updatedAt;
        this.vendorUniqueId = vendorUniqueId;
        this.allMaterialsSubmittals = allMaterialsSubmittals;
        this.materialsSubmittals = materialsSubmittals;
        this.project = project;
        this.vendorCache = vendorCache;
    }
    /* eslint-enable max-statements */

    // oxlint-disable-next-line complexity
    static fromApiObject(json: any, timezone: string): Submittal {
        const newObject = new Submittal(
            json["id"],
            json["approval_date"] ? dayjs(json["approval_date"]).tz(timezone) : null,
            dayjs(json["created_at"]).tz(timezone),
            json["current_revision"],
            json["description"],
            json["label"],
            json["latest_response"],
            json["lead_time"],
            json["needs_revise_and_resubmit"],
            json["project_id"],
            json["required_on_jobsite"] ? dayjs(json["required_on_jobsite"]).tz(timezone) : null,
            json["spec_number"],
            json["status"],
            json["submit_date"] ? dayjs(json["submit_date"]).tz(timezone) : null,
            json["submittal_number"],
            json["submittal_type"],
            json["title"],
            dayjs(json["updated_at"]).tz(timezone),
            json["vendor_unique_id"],
            json["all_materials_submittals"]?.map((item: any) => MaterialsSubmittal.fromApiObject(item, timezone)),
            json["materials_submittals"]?.map((item: any) => MaterialsSubmittal.fromApiObject(item, timezone)),
            json["project"] == null ? json["project"] : Project.fromApiObject(json["project"], timezone),
            json["vendor_cache"] == null
                ? json["vendor_cache"]
                : VendorCache.fromApiObject(json["vendor_cache"], timezone)
        );

        return newObject;
    }

    // oxlint-disable-next-line complexity
    toApiObject(visited = new Set<any>()): any {
        if (visited.has(this)) {
            // Return null, undefined, or primary key to break the cycle
            return undefined;
        }
        visited.add(this);

        return {
            id: this.id,
            approval_date: this.approvalDate,
            created_at: this.createdAt,
            current_revision: this.currentRevision,
            description: this.description,
            label: this.label,
            latest_response: this.latestResponse,
            lead_time: this.leadTime,
            needs_revise_and_resubmit: this.needsReviseAndResubmit,
            project_id: this.projectId,
            required_on_jobsite: this.requiredOnJobsite,
            spec_number: this.specNumber,
            status: this.status,
            submit_date: this.submitDate,
            submittal_number: this.submittalNumber,
            submittal_type: this.submittalType,
            title: this.title,
            updated_at: this.updatedAt,
            vendor_unique_id: this.vendorUniqueId,
            all_materials_submittals: this.allMaterialsSubmittals
                ?.map((item) => item.toApiObject(visited))
                .filter((item) => item !== undefined),
            materials_submittals: this.materialsSubmittals
                ?.map((item) => item.toApiObject(visited))
                .filter((item) => item !== undefined),
            project_attributes: this.project?.toApiObject(visited),
            vendor_cache_attributes: this.vendorCache?.toApiObject(visited),
            _destroy: this._destroy,
        };
    }

    // NOTE: This could potentially cause infinite loops if the object graph is
    // circular.
    inAlternateTimezone(timezone: string): Submittal {
        const clone = Object.create(this) as Submittal;

        for (const key of Object.keys(this)) {
            if (this[key] instanceof dayjs) {
                // @ts-expect-error: Typescript thinks that this is not a dayjs object even though it is checked on the line before
                clone[key] = this[key].tz(timezone);
            } else if (Array.isArray(this[key])) {
                clone[key] = this[key].map((item) =>
                    item.inAlternateTimezone ? item.inAlternateTimezone(timezone) : item
                );
            } else if (this[key] && this[key].inAlternateTimezone) {
                clone[key] = this[key].inAlternateTimezone(timezone);
            }
        }

        return clone;
    }
}
